// Attach-to-existing flow: the simplified incident form (description +
// environment + optional attachment) that POSTs /api/qc/incidents with
// case_id set. Used by the new-case modal's "Attach my report to this case"
// switch and by case detail's "I'm seeing this too".

import { useState } from 'react';
import { QC_ENVIRONMENTS, type QcEnvironment } from '@itms-qc/shared';
import { api, uploadAttachments } from '../lib/api';
import { useApiToken } from '../auth/useApiToken';
import { AttachmentPicker, type PickedFile } from './AttachmentPicker';
import {
  dangerTextStyle,
  fieldGap,
  labelStyle,
  primaryButtonStyle,
  selectStyle,
  textareaStyle,
} from './form';

interface Props {
  caseId: number;
  initialDescription?: string;
  submitLabel?: string;
  onDone: (result: { reopened: boolean }) => void;
}

export function IncidentForm({
  caseId,
  initialDescription = '',
  submitLabel = 'Add my report',
  onDone,
}: Props) {
  const getToken = useApiToken();
  const [description, setDescription] = useState(initialDescription);
  const [environment, setEnvironment] = useState<QcEnvironment | ''>('');
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!description.trim()) {
      setError('Describe what you saw — that is the one required field.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const attachmentUrls = files.length
        ? await uploadAttachments(token, files.map((f) => f.file))
        : [];
      const result = await api.attachIncident(token, caseId, {
        description: description.trim(),
        environment: environment || null,
        attachmentUrls,
      });
      onDone({ reopened: result.reopened });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add your report');
      setSubmitting(false);
    }
  }

  return (
    <div>
      <div style={fieldGap}>
        <label style={labelStyle}>
          What are you seeing?
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened, where, and what you expected instead…"
            style={{ ...textareaStyle, marginTop: 4 }}
          />
        </label>
      </div>

      <div style={{ width: 160, ...fieldGap }}>
        <label style={labelStyle}>
          Environment
          <select
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as QcEnvironment | '')}
            style={{ ...selectStyle, marginTop: 4 }}
          >
            <option value="">Not sure</option>
            {QC_ENVIRONMENTS.map((env) => (
              <option key={env} value={env}>
                {env}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div style={fieldGap}>
        <AttachmentPicker files={files} setFiles={setFiles} disabled={submitting} />
      </div>

      {error && <div style={{ ...dangerTextStyle, marginBottom: 12 }}>{error}</div>}

      <button type="button" onClick={() => void submit()} disabled={submitting} style={{ ...primaryButtonStyle, opacity: submitting ? 0.6 : 1 }}>
        {submitting ? 'Submitting…' : submitLabel}
      </button>
    </div>
  );
}
